import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../lib/data";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "../lib/auth";
import { useLang } from "../context/LanguageContext";
import { Button } from "./ui/button";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const { lang, toggle, t } = useLang();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const testid = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="main-navbar">
      {/* Top strip */}
      <div className="hidden sm:block bg-forest-800 text-cream-100 text-[11px] tracking-wide text-center py-1 font-medium">
        <span className="text-saffron-500">✦</span> GENESIS MALL — All-in-One Business Center · Rooted in Bharat. Connected to Opportunity.
      </div>
      <div className={`transition-all duration-300 border-b ${scrolled ? "bg-cream-100/95 backdrop-blur-md border-cream-300 shadow-sm py-2" : "bg-cream-100/90 border-cream-200 py-3"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-2">
          <Logo onLight />

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV_LINKS.map((link) => {
              const active = location.pathname === link.to;
              return (
                <Link key={link.to} to={link.to} data-testid={`nav-link-${testid(link.label)}`}
                  className={`px-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors whitespace-nowrap ${active ? "text-terracotta-600" : "text-soil-700 hover:text-forest-700"}`}>
                  {t(link.label)}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <>
                <NotificationBell />
                <Link to="/dashboard" data-testid="navbar-dashboard-button">
                  <Button className="forest-gradient text-cream-50 font-bold hover:opacity-90 rounded-full px-5">{t("dashboard")}</Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-button" className="text-sm font-semibold text-soil-700 hover:text-forest-700 px-3 py-2 rounded-lg">{t("login")}</Link>
                <Link to="/register" data-testid="nav-register-button">
                  <Button className="forest-gradient text-cream-50 font-bold hover:opacity-90 rounded-full px-6">Register</Button>
                </Link>
              </>
            )}
            <button onClick={toggle} data-testid="lang-toggle" className="ml-1 text-xs font-bold text-terracotta-600 border border-terracotta-500/40 rounded-full px-3 py-1.5 hover:bg-terracotta-100 transition-colors">
              {lang === "en" ? "हिंदी" : "ENG"}
            </button>
          </div>

          <div className="lg:hidden flex items-center gap-1.5">
            {user && <NotificationBell />}
            <button className="text-soil-700 p-2" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-cream-100 border-b border-cream-300 shadow-lg max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link key={link.to} to={link.to} data-testid={`mobile-nav-link-${testid(link.label)}`}
                className={`px-4 py-3 rounded-lg text-base font-medium ${location.pathname === link.to ? "text-terracotta-600 bg-cream-200" : "text-soil-700"}`}>
                {t(link.label)}
              </Link>
            ))}
            {user ? (
              <Link to="/dashboard" data-testid="mobile-dashboard-link" className="mt-2">
                <Button className="w-full forest-gradient text-cream-50 font-bold rounded-full">{t("dashboard")}</Button>
              </Link>
            ) : (
              <div className="flex gap-2 mt-2">
                <Link to="/login" data-testid="mobile-login-link" className="flex-1 text-center py-3 text-soil-700 font-semibold border border-cream-300 rounded-full">{t("login")}</Link>
                <Link to="/register" data-testid="mobile-register-link" className="flex-1">
                  <Button className="w-full forest-gradient text-cream-50 font-bold rounded-full">Register</Button>
                </Link>
              </div>
            )}
            <button onClick={toggle} data-testid="mobile-lang-toggle" className="text-center py-3 text-terracotta-600 font-semibold border border-terracotta-500/30 rounded-full mt-1">
              {lang === "en" ? "हिंदी में देखें" : "View in English"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
